import { Box, IconButton, makeStyles, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import React, { useEffect, useRef, useState } from 'react';
import { getOwnerMeters } from '../../services/meters';
import { blueBar } from '../../helpers/common';
import HelpIcon from '@mui/icons-material/Help';
import { borderRadiusBoxes, themePalette } from '../Dashboards/State/stateTreeStyles';

const useStyles = makeStyles(theme => {
	const scrollableList = {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.35),
		minHeight: theme.spacing(12),
		maxHeight: theme.spacing(15),
		overflowY: 'auto',
		overflowX: 'hidden',
		paddingRight: theme.spacing(0.5),
		scrollbarWidth: 'thin',
		scrollbarColor: `${themePalette.status.scrollbarThumb} ${themePalette.status.scrollbarTrackLight}`,
		'&::-webkit-scrollbar': {
			width: 6,
		},
		'&::-webkit-scrollbar-track': {
			background: themePalette.status.scrollbarTrackLight,
			borderRadius: borderRadiusBoxes,
		},
		'&::-webkit-scrollbar-thumb': {
			background: themePalette.status.scrollbarThumb,
			borderRadius: 999,
		},
	};

	return ({
	label: {
		fontSize: 16,
		fontWeight: 700,
		marginTop: theme.spacing(2),
	},
	deviceSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
	},
	deviceTransferLayout: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
		alignItems: 'stretch',
		columnGap: theme.spacing(1),
	},
	devicePanel: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		padding: theme.spacing(1),
		borderRadius: borderRadiusBoxes,
		border: `1px solid ${themePalette.border.soft}`,
		background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)`,
	},
	devicesTitle: {
		fontSize: '13px',
		color: theme.palette.grey.main,
		backgroundColor: theme.palette.grey.dark,
		padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}`,
		borderRadius: borderRadiusBoxes,
		textAlign: 'center',
		marginBottom: theme.spacing(1),
	},
	deviceList: {
		...scrollableList,
	},
	deviceItem: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		minWidth: 0,
		minHeight: theme.spacing(4),
		boxSizing: 'border-box',
		border: `1px solid ${themePalette.border.subtle}`,
		borderRadius: borderRadiusBoxes,
		textAlign: 'left',
		fontSize: '15px',
		color: themePalette.text.primary,
		lineHeight: 1.3,
		padding: theme.spacing(0.6, 1),
		cursor: 'pointer',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		backgroundColor: themePalette.common.white,
		transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
		'&:hover': {
			color: '#fff',
			background: themePalette.primary.main,
			borderColor: themePalette.primary.main,
		},
	},
	selectedItem: {
		background: themePalette.primary.main,
		borderColor: themePalette.primary.main,
		color: '#fff',
	},
	active: {
		backgroundColor: themePalette.primary.main,
		borderColor: themePalette.primary.main,
		color: '#fff',
	},
	emptySelectedDevice: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: theme.spacing(12),
		padding: theme.spacing(1),
		fontSize: '15px',
		textAlign: 'center',
		backgroundColor: themePalette.warning.light,
		color: themePalette.warning.dark,
		border: `1px solid rgba(180, 83, 9, 0.22)`,
		borderRadius: borderRadiusBoxes,
		lineHeight: 1.25,
		wordBreak: 'break-word',
	},
	actionsColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(1),
	},
	actionButton: {
		padding: theme.spacing(0.45),
		border: `1px solid ${themePalette.border.soft}`,
		borderRadius: borderRadiusBoxes,
		backgroundColor: themePalette.common.white,
	},
	selectedList: {
		...scrollableList,
	},
	helpMessage: {
		marginTop: '10px',
		color: 'green',
		display: 'flex',
		alignItems: 'center',
	},
	'@media (max-width: 600px)': {
		deviceTransferLayout: {
			gridTemplateColumns: '1fr',
			rowGap: theme.spacing(0.85),
		},
		devicePanel: {
			padding: theme.spacing(0.85),
		},
		devicesTitle: {
			fontSize: '12px',
			padding: `${theme.spacing(0.45)}px ${theme.spacing(1.1)}px`,
			marginBottom: theme.spacing(0.75),
		},
		deviceList: {
			minHeight: 0,
			maxHeight: theme.spacing(16),
		},
		deviceItem: {
			fontSize: '14px',
			minHeight: theme.spacing(4.25),
			padding: theme.spacing(0.55, 0.8),
		},
		emptySelectedDevice: {
			minHeight: theme.spacing(8),
			fontSize: '14px',
			padding: theme.spacing(0.85),
		},
		actionsColumn: {
			flexDirection: 'row',
			justifyContent: 'center',
		},
		actionButton: {
			padding: theme.spacing(0.35),
		},
		selectedList: {
			minHeight: 0,
			maxHeight: theme.spacing(16),
		},
		helpMessage: {
			alignItems: 'flex-start',
			lineHeight: 1.35,
		},
	},
	});
});

export default function DevicesList({
	savedMeter, savedMeterName, selectedMeter, selectedMeterName,
	unassignedMeters, setSelectedMeter, setSelectedMeterName,
	setSelectedAutomates, selectedAutomates, nodeData, unassignedAutomates, setUnassignedAutomates, setSelectedMeterAux, setIsSettingsButtonDisabled
}) {
	const blueLine = blueBar();
	const classes = useStyles();
	const isMobile = useMediaQuery('(max-width:600px)');
	const isMountedRef = useRef(true);
	const [freeMeterSelected, setFreeMeterSelected] = useState({ id: 0, name: "" });
	const [freeAutomateSelected, setFreeAutomateSelected] = useState({ id: 0, name: "" });
	const [removeAutomateSelected, setRemoveAutomateSelected] = useState({});

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (nodeData?.automates?.length > 0) {
			setSelectedAutomates(nodeData?.automates);
		} else {
			setSelectedAutomates([]);
		}
		// eslint-disable-next-line
	}, []);

	const selectMeterId = async (meterId) => {
		if (!meterId) return;

		try {
			const res = await getOwnerMeters();
			if (!isMountedRef.current) return;
			const findElement = res.data.find((element) => element.id === meterId);
			setSelectedMeterAux(findElement);
		} catch (error) {
		}
	};

	const handleSelectedMeter = (meterId, meterName) => {
		setSelectedMeter(meterId);
		setSelectedMeterName(meterName);
		selectMeterId(meterId);
		setIsSettingsButtonDisabled(true);
	};

	const toggleSelected = (id, meterName, type) => {
		if (type === 'automate') {
			if (freeAutomateSelected.id !== id) {
				setFreeAutomateSelected({ id, name: meterName });
			} else {
				setFreeAutomateSelected({ id: 0, name: "" });
			}
			return;
		}

		if (freeMeterSelected.id !== id) {
			setFreeMeterSelected({ id, name: meterName });
		} else {
			setFreeMeterSelected({ id: 0, name: "" });
		}
	};

	const toggleSelectedRemoveAutomate = (id, name) => {
		setRemoveAutomateSelected({
			nombre: name,
			id,
		});
	};

	const handleSelectedAutomate = (id, name) => {
		const validate = selectedAutomates.find((element) => element.id === id);
		if (validate?.id) return;

		const obj = { nombre: name, id };
		const filter = unassignedAutomates.filter((element) => element.id !== id);
		setUnassignedAutomates(filter);
		setSelectedAutomates([
			...selectedAutomates,
			obj,
		]);
	};

	const handleRemoveAutomate = (id) => {
		const findElementRemove = selectedAutomates.find((element) => element.id === id);
		const filter = selectedAutomates.filter((element) => element.id !== id);
		const obj = {
			nombre: findElementRemove?.nombre,
			id: findElementRemove?.id,
		};

		setUnassignedAutomates([
			...unassignedAutomates,
			obj,
		]);
		setSelectedAutomates(filter);
		setRemoveAutomateSelected({});
	};

	const isAutomateAssigned = (automateId) =>
		selectedAutomates?.some((automate) => automate.id === automateId);

	const checkLimitDevicesAssigned = () => {
		if (selectedAutomates?.length > 0) return true;
		if (selectedMeter) return true;
		return false;
	};

	const exceedLimitAutomatesAndMetersAssigned = (type) => {
		if (type === 'medidor') {
			if (selectedMeter) return true;
			if (selectedAutomates.length > 0) return true;
			return false;
		}

		if (selectedMeter) return true;
		if (selectedAutomates?.length > 0) return true;
		return false;
	};

	const meterOptions = [
		...(savedMeter && selectedMeter !== savedMeter ? [{ id: savedMeter, nombre: savedMeterName }] : []),
		...((unassignedMeters || []).filter((meter) => selectedMeter !== meter.id)),
	];

	const renderSelectableDevice = ({ id, name, isActive, onClick, isSelected = false }) => (
		<Box
			key={id}
			className={`${classes.deviceItem} ${isSelected ? classes.selectedItem : ''} ${isActive ? classes.active : ''}`}
			onClick={onClick}
			title={name}
		>
			{name}
		</Box>
	);

	return (
		<Box>
			<Box className={classes.deviceSection}>
				<Box component='label' display='block' className={classes.label}>
					Dispositivos de medicion
				</Box>
				<Box className={classes.deviceTransferLayout}>
					<Box className={classes.devicePanel}>
						<Box className={classes.devicesTitle}>Dispositivos libres</Box>
						<Box className={classes.deviceList}>
							{meterOptions.map((meter) =>
								renderSelectableDevice({
									id: meter.id,
									name: meter.nombre,
									isActive: freeMeterSelected.id === meter.id,
									onClick: () => toggleSelected(meter.id, meter.nombre),
								})
							)}
						</Box>
					</Box>

					<Box className={classes.actionsColumn}>
						<IconButton
							aria-label='seleccionar'
							className={classes.actionButton}
							onClick={() => {
								setFreeMeterSelected({ id: 0, name: "" });
								handleSelectedMeter(freeMeterSelected.id, freeMeterSelected.name);
							}}
							disabled={exceedLimitAutomatesAndMetersAssigned('medidor') || !Boolean(freeMeterSelected.id)}
						>
							<ChevronRightIcon fontSize={isMobile ? 'small' : 'inherit'} />
						</IconButton>
						<IconButton
							aria-label='deseleccionar'
							className={classes.actionButton}
							onClick={() => handleSelectedMeter(0, "")}
							disabled={!Boolean(selectedMeter)}
						>
							<ChevronLeftIcon fontSize={isMobile ? 'small' : 'inherit'} />
						</IconButton>
					</Box>

					<Box className={classes.devicePanel}>
						<Box className={classes.devicesTitle}>Dispositivo asignado</Box>
						{selectedMeter ? (
							<Box className={classes.selectedList}>
								{renderSelectableDevice({
									id: selectedMeter,
									name: selectedMeterName,
									isSelected: true,
								})}
							</Box>
						) : (
							<Box className={classes.emptySelectedDevice}>Ningun dispositivo seleccionado</Box>
						)}
					</Box>
				</Box>
			</Box>

			<Box style={{
				backgroundColor: '#2586bc69',
				height: '1px ',
				width: ' 100 %',
				margin: '20px 0 20px 0',
			}} />

			<Box className={classes.deviceSection}>
				<Box component='label' display='block' className={classes.label}>
					Dispositivos automate
				</Box>
				<Box className={classes.deviceTransferLayout}>
					<Box className={classes.devicePanel}>
						<Box className={classes.devicesTitle}>Dispositivos libres</Box>
						<Box className={classes.deviceList}>
							{(unassignedAutomates || []).map((automate) =>
								!isAutomateAssigned(automate.id)
									? renderSelectableDevice({
										id: automate.id,
										name: automate.nombre,
										isActive: freeAutomateSelected.id === automate.id,
										onClick: () => toggleSelected(automate.id, automate.nombre, 'automate'),
									})
									: null
							)}
						</Box>
					</Box>

					<Box className={classes.actionsColumn}>
						<IconButton
							aria-label='seleccionar'
							className={classes.actionButton}
							onClick={() => {
								setFreeAutomateSelected({ id: 0, name: "" });
								handleSelectedAutomate(freeAutomateSelected.id, freeAutomateSelected.name);
							}}
							disabled={exceedLimitAutomatesAndMetersAssigned() || !Boolean(freeAutomateSelected.id)}
						>
							<ChevronRightIcon fontSize={isMobile ? 'small' : 'inherit'} />
						</IconButton>
						<IconButton
							aria-label='deseleccionar'
							className={classes.actionButton}
							onClick={() => handleRemoveAutomate(removeAutomateSelected.id)}
							disabled={!Boolean(removeAutomateSelected.id)}
						>
							<ChevronLeftIcon fontSize={isMobile ? 'small' : 'inherit'} />
						</IconButton>
					</Box>

					<Box className={classes.devicePanel}>
						<Box className={classes.devicesTitle}>Dispositivo asignado</Box>
						{selectedAutomates?.length > 0 ? (
							<Box className={classes.selectedList}>
								{selectedAutomates.map((element) =>
									renderSelectableDevice({
										id: element.id,
										name: element.nombre,
										isActive: removeAutomateSelected.id === element.id,
										isSelected: true,
										onClick: () => toggleSelectedRemoveAutomate(element.id, element.nombre),
									})
								)}
							</Box>
						) : (
							<Box className={classes.emptySelectedDevice}>Ningun dispositivo seleccionado</Box>
						)}
					</Box>
				</Box>
			</Box>

			{checkLimitDevicesAssigned() && (
				<>
					{blueLine}
					<Typography variant="body2" className={classes.helpMessage}>
						Ya tiene un dispositivo asignado. No es posible agregar otro
						<Tooltip title="Solo podra asignar un dispositivo por nodo, ya sea un Automate o un medidor.">
							<HelpIcon style={{ marginLeft: 6, fontSize: 22, cursor: 'pointer' }} />
						</Tooltip>
					</Typography>
				</>
			)}

			{blueLine}
		</Box>
	);
}
